import { SpriteIcon } from "@/components/icons/IconSprite";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { LazyAuthModal as AuthModal } from "@/components/auth/LazyAuthModal";
import { listMyFavoriteSlugs, toggleFavorite } from "@/lib/favorites.functions";
import { useLocale } from "@/lib/i18n/locale-context";

/** Delad fråga så att alla hjärtan på sidan vet samma sak. */
export const favoriteSlugsQuery = {
  queryKey: ["favorite-slugs"] as const,
  queryFn: () => listMyFavoriteSlugs(),
  staleTime: 30_000,
};

export function FavoriteButton({
  slug,
  size = "md",
  shape = "square",
  className = "",
}: {
  slug: string;
  size?: "sm" | "md" | "lg";
  shape?: "square" | "round";
  className?: string;
}) {
  const { user } = useAuth();
  const { lang } = useLocale();
  const t = lang === "es" ? { saved: "Guardado en favoritos.", removed: "Eliminado de favoritos.", error: "No ha sido posible. Inténtalo de nuevo.", remove: "Eliminar de guardados", save: "Guardar anuncio" } : lang === "en" ? { saved: "Saved to favourites.", removed: "Removed from favourites.", error: "That did not work. Try again.", remove: "Remove from saved", save: "Save listing" } : { saved: "Sparad bland dina favoriter.", removed: "Borttagen från favoriter.", error: "Det gick inte just nu. Försök igen.", remove: "Ta bort från sparade", save: "Spara annonsen" };
  const qc = useQueryClient();
  const [authOpen, setAuthOpen] = useState(false);
  const toggle = useServerFn(toggleFavorite);

  const { data: slugs } = useQuery({ ...favoriteSlugsQuery, enabled: !!user });
  const active = !!slugs?.includes(slug);

  const mutation = useMutation({
    mutationFn: () => toggle({ data: { slug } }),
    onSuccess: (res) => {
      qc.setQueryData(["favorite-slugs"], (prev: string[] | undefined) => {
        const list = prev ?? [];
        return res.favorited ? [...new Set([...list, slug])] : list.filter((s) => s !== slug);
      });
      void qc.invalidateQueries({ queryKey: ["my-favorites"] });
       toast.success(res.favorited ? t.saved : t.removed);
    },
     onError: () => toast.error(t.error),
  });

  const box = size === "sm" ? "h-8 w-8" : size === "lg" ? "h-11 w-11" : "h-9 w-9";
  const icon = size === "sm" ? "h-4 w-4" : size === "lg" ? "h-5 w-5" : "h-[18px] w-[18px]";
  const radius = shape === "round" ? "rounded-full" : "rounded-md";

  return (
    <>
      <button
        type="button"
         aria-label={active ? t.remove : t.save}
        aria-pressed={active}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!user) {
            setAuthOpen(true);
            return;
          }
          mutation.mutate();
        }}
        disabled={mutation.isPending}
        className={`inline-flex ${box} ${radius} items-center justify-center border border-border bg-background/90 text-muted-foreground shadow-sm backdrop-blur transition hover:text-primary disabled:opacity-60 ${className}`}
      >
        <SpriteIcon
          name="heart"
          className={`${icon} transition ${active ? "fill-primary text-primary" : ""}`}
          strokeWidth={2}
        />
      </button>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </>
  );
}
